import React, { useState, useEffect } from 'react';
import BackToDashboardBtn from './BackToDashboardBtn';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../components/styles/TrainerProfile.css';
import { useTranslation } from '../../i18n';
import API_BASE_URL from '../../config/api';

const resolveTrainerImageSrc = (value) => {
  if (!value || typeof value !== 'string') return '/uploads/trainers/default.jpg';

  const src = value.trim();
  if (!src) return '/uploads/trainers/default.jpg';

  // Imágenes persistidas en Atlas como base64.
  if (src.startsWith('data:image/')) return src;

  // URLs completas.
  if (src.startsWith('http://') || src.startsWith('https://')) return src;

  // Rutas públicas del backend.
  if (src.startsWith('/uploads/')) return `${API_BASE_URL}${src}`;

  // Compatibilidad legacy: solo nombre de archivo.
  return `${API_BASE_URL}/uploads/trainers/${src}`;
};

const TrainerProfile = () => {
  const { t } = useTranslation();

  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    gender: '',
    club: '',
    country: '',
    city: '',
    disciplines: [],
    profilePhoto: '',
    clubPhoto: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [message, setMessage] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const disciplines = ['Trail', 'Running', 'Ciclismo', 'Natación', 'Triatlón'];

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userId = localStorage.getItem('userId');
        if (!userId) {
          setMessage(t('trainerProfile.messages.userIdNotFound'));
          setLoading(false);
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/api/trainers/profile/${userId}`);
        setProfileData(response.data.trainer || {});
        setLoading(false);
      } catch (error) {
        console.error('❌ Error al cargar el perfil:', error.response?.data || error.message);
        setMessage(t('trainerProfile.messages.loadError'));
        setLoading(false);
      }
    };

    fetchProfile();
  }, [t]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData({ ...profileData, [name]: value });
  };

  const handleToggleDiscipline = (discipline) => {
    setProfileData((prev) => ({
      ...prev,
      disciplines: prev.disciplines.includes(discipline)
        ? prev.disciplines.filter((d) => d !== discipline)
        : [...prev.disciplines, discipline],
    }));
  };

  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append(type, file);

    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setMessage(t('trainerProfile.messages.userIdNotFound'));
        return;
      }

      const endpoint = type === 'profilePhoto'
        ? `${API_BASE_URL}/api/trainers/upload-photo/${userId}`
        : `${API_BASE_URL}/api/trainers/upload-club-photo/${userId}`;

      const response = await axios.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const uploadedPhoto =
        response.data?.[type] ||
        response.data?.profilePhoto ||
        response.data?.clubPhoto ||
        response.data?.filePath ||
        response.data?.user?.[type] ||
        '';

      if (uploadedPhoto) {
        setProfileData((prev) => ({
          ...prev,
          [type]: uploadedPhoto,
        }));
      }

      setMessage(
        type === 'profilePhoto'
          ? t('trainerProfile.messages.profilePhotoUpdated')
          : t('trainerProfile.messages.clubPhotoUpdated')
      );
    } catch (error) {
      console.error(`❌ Error al subir ${type}:`, error.response?.data || error.message);
      setMessage(
        type === 'profilePhoto'
          ? t('trainerProfile.messages.profilePhotoUploadError')
          : t('trainerProfile.messages.clubPhotoUploadError')
      );
    }
  };

  const handleSaveProfile = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setMessage(t('trainerProfile.messages.userIdNotFound'));
        return;
      }

      await axios.post(`${API_BASE_URL}/api/trainers/save-profile`, {
        userId,
        ...profileData,
      });

      setMessage(t('trainerProfile.messages.profileSaved'));
      setEditMode(false);
    } catch (error) {
      console.error('❌ Error al guardar el perfil:', error.response?.data || error.message);
      setMessage(t('trainerProfile.messages.saveError'));
    }
  };

  const handlePasswordChange = async () => {
    try {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        setMessage(t('trainerProfile.messages.userIdNotFound'));
        return;
      }

      if (!profileData.password || !profileData.confirmPassword) {
        setMessage(t('trainerProfile.messages.passwordFieldsRequired'));
        return;
      }

      if (profileData.password !== profileData.confirmPassword) {
        setMessage(t('trainerProfile.messages.passwordMismatch'));
        return;
      }

      await axios.post(`${API_BASE_URL}/auth/change-password`, {
        userId,
        currentPassword: profileData.password,
        newPassword: profileData.confirmPassword,
      });

      setMessage(t('trainerProfile.messages.passwordUpdated'));
      setProfileData((prev) => ({ ...prev, password: '', confirmPassword: '' }));
    } catch (error) {
      console.error('❌ Error al actualizar contraseña:', error.response?.data || error.message);
      setMessage(t('trainerProfile.messages.passwordUpdateError'));
    }
  };

  if (loading) return <p>{t('trainerProfile.loading')}</p>;

  return (
    <div className="athlete-profile-container">
      <div style={{ padding:"12px 16px 8px", textAlign:"left" }}><BackToDashboardBtn /></div>
      <div className="profile-grid updated-layout">
        <section className="profile-card">
          <h2>{t('trainerProfile.photos')}</h2>

          <div className="photo-section">
            <label>{t('trainerProfile.profilePhoto')}:</label>
            <img
              src={resolveTrainerImageSrc(profileData.profilePhoto)}
              alt={t('trainerProfile.profilePhotoAlt')}
              className="profile-img"
            />
            {editMode && <input type="file" name="profilePhoto" onChange={(e) => handleFileChange(e, 'profilePhoto')} />}
          </div>

          <div className="photo-section">
            <label>{t('trainerProfile.clubPhoto')}:</label>
            <img
              src={resolveTrainerImageSrc(profileData.clubPhoto)}
              alt={t('trainerProfile.clubPhotoAlt')}
              className="profile-img"
            />
            {editMode && <input type="file" name="clubPhoto" onChange={(e) => handleFileChange(e, 'clubPhoto')} />}
          </div>
        </section>

        <section className="profile-card">
          <h2>{t('trainerProfile.generalInfo')}</h2>

          <p>
            <strong>{t('trainerProfile.username')}:</strong>{' '}
            {editMode ? (
              <input
                type="text"
                name="username"
                value={profileData.username || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.username
            )}
          </p>

          <p>
            <strong>{t('trainerProfile.email')}:</strong>{' '}
            {editMode ? (
              <input
                type="email"
                name="email"
                value={profileData.email || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.email
            )}
          </p>

          <p>
            <strong>{t('trainerProfile.club')}:</strong>{' '}
            {editMode ? (
              <input
                type="text"
                name="club"
                value={profileData.club || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.club
            )}
          </p>

          <p><strong>{t('trainerProfile.trainer')}:</strong></p>

          <p>
            <label>{t('trainerProfile.firstName')}:</label>{' '}
            {editMode ? (
              <input
                type="text"
                name="firstName"
                value={profileData.firstName || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.firstName
            )}
          </p>

          <p>
            <label>{t('trainerProfile.lastName')}:</label>{' '}
            {editMode ? (
              <input
                type="text"
                name="lastName"
                value={profileData.lastName || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.lastName
            )}
          </p>

          <p>
            <label>{t('trainerProfile.gender')}:</label>{' '}
            {editMode ? (
              <select
                name="gender"
                value={profileData.gender || ''}
                onChange={handleInputChange}
              >
                <option value="">{t('trainerProfile.select')}</option>
                <option value="Masculino">{t('trainerProfile.genderOptions.male')}</option>
                <option value="Femenino">{t('trainerProfile.genderOptions.female')}</option>
                <option value="Otro">{t('trainerProfile.genderOptions.other')}</option>
              </select>
            ) : (
              profileData.gender || t('trainerProfile.notSpecified')
            )}
          </p>

          <p>
            <strong>{t('trainerProfile.country')}:</strong>{' '}
            {editMode ? (
              <input
                type="text"
                name="country"
                value={profileData.country || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.country
            )}
          </p>

          <p>
            <strong>{t('trainerProfile.city')}:</strong>{' '}
            {editMode ? (
              <input
                type="text"
                name="city"
                value={profileData.city || ''}
                onChange={handleInputChange}
              />
            ) : (
              profileData.city
            )}
          </p>
        </section>

        <section className="profile-card">
          <h2>{t('trainerProfile.disciplines')}</h2>
		{disciplines.map((discipline, index) => (
		  <button
			key={index}
			type="button"
			className={`discipline-btn ${
			  Array.isArray(profileData.disciplines) &&
			  profileData.disciplines.includes(discipline)
				? 'selected'
				: ''
			}`}
			onClick={() => handleToggleDiscipline(discipline)}
			disabled={!editMode}
		  >
			{t(`trainerProfile.disciplineOptions.${discipline}`, discipline)}
		  </button>
		))}
        </section>

        <section className="profile-card">
          <h2>{t('trainerProfile.changePassword')}</h2>
          <p>
            <input
              type="password"
              name="password"
              placeholder={t('trainerProfile.currentPasswordPlaceholder')}
              value={profileData.password}
              onChange={handleInputChange}
            />
          </p>
          <p>
            <input
              type="password"
              name="confirmPassword"
              placeholder={t('trainerProfile.newPasswordPlaceholder')}
              value={profileData.confirmPassword}
              onChange={handleInputChange}
            />
          </p>
          <button className="btn-change-password" onClick={handlePasswordChange}>
            {t('trainerProfile.update')}
          </button>
        </section>
      </div>

      <div className="profile-actions centered-buttons">
        {editMode ? (
          <button type="button" onClick={handleSaveProfile}>
            {t('trainerProfile.saveChanges')}
          </button>
        ) : (
          <button type="button" onClick={() => setEditMode(true)}>
            {t('trainerProfile.editProfile')}
          </button>
        )}

      </div>

      {message && <p>{message}</p>}
    </div>
  );
};

export default TrainerProfile;
